/**
 * API Client for EVOP Inspector Frontend
 * 
 * Handles all communication with the backend API
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { getApiUrl } from '../utils/urls'
import { getCSRFToken } from '../utils/cookies'
import { logger } from '../utils/logger'
import type { FranchisePartnerResource } from '../types/resources'

// Timeout configuration for different operation types
const TIMEOUT_CONFIG = {
  default: 30000,          // 30 seconds for most operations
  extended: 120000,        // 2 minutes for longer operations (matches AWS App Runner limit)
  dataManagement: 300000,  // 5 minutes for data clear/bulk operations
  largeSync: 600000,       // 10 minutes for large sync operations
}

// Create axios instance with default configuration
const createApiClient = (): AxiosInstance => {
  // Use relative API path - nginx handles routing to backend
  const baseURL = getApiUrl()

  const client = axios.create({
    baseURL: baseURL,
    timeout: TIMEOUT_CONFIG.default,
    withCredentials: true, // Include cookies with all requests (matches admin section)
  })

  // Request interceptor for logging, cache control, and CSRF protection
  client.interceptors.request.use(
    (config) => {
      logger.debug(`🔍 API Request: ${config.method?.toUpperCase()} ${config.url}`)

      // Apply endpoint-specific timeouts
      if (config.url?.includes('/sync/clear-data') ||
          config.url?.includes('/sync/bulk-') ||
          config.url?.includes('/data-management/')) {
        config.timeout = TIMEOUT_CONFIG.dataManagement
        logger.debug(`⏱️ Extended timeout applied: ${config.timeout / 1000}s`)
      } else if (config.url?.includes('/sync/trigger') &&
                 config.method?.toLowerCase() === 'post') {
        config.timeout = TIMEOUT_CONFIG.largeSync
        logger.debug(`⏱️ Large sync timeout applied: ${config.timeout / 1000}s`)
      } else if (config.url?.includes('/admin-tools/bulk-operations')) {
        config.timeout = TIMEOUT_CONFIG.extended
        logger.debug(`⏱️ Extended timeout applied: ${config.timeout / 1000}s`)
      }

      // Add CSRF token and Content-Type for state-changing requests
      const method = config.method?.toUpperCase()
      if (method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        const csrfToken = getCSRFToken()
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken
          logger.debug('🔒 Added CSRF token to request')
        } else {
          logger.warn('⚠️ No CSRF token found for state-changing request')
        }

        // Only set Content-Type for requests with a body (POST, PUT, PATCH)
        // Don't set it for DELETE or GET to avoid interfering with blob responses
        if (method !== 'DELETE' && config.data && !config.headers['Content-Type']) {
          config.headers['Content-Type'] = 'application/json'
        }
      }
      
      // Add no-cache headers for sync-related endpoints to prevent stale data
      if (config.url?.includes('/sync/operations/active') || 
          config.url?.includes('/sync/') && config.method?.toLowerCase() === 'get') {
        config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate'
        config.headers['Pragma'] = 'no-cache'
        config.headers['Expires'] = '0'
      }
      
      return config
    },
    (error) => {
      logger.error('❌ API Request Error:', error.message || 'Unknown error')
      return Promise.reject(error)
    }
  )

  // Response interceptor for logging and error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      logger.debug(`✅ API Response: ${response.status} ${response.config.url}`)
      const requestId = response.headers?.['x-request-id'] || response.headers?.['X-Request-Id']
      if (requestId) {
        lastRequestId = String(requestId)
      }
      return response
    },
    async (error) => {
      logger.error('❌ API Response Error:', error.message || 'Unknown error')

      // Handle specific error cases
      if (error.response?.status === 401) {
        // Don't log authentication warnings for public invitation endpoints
        const isInvitationEndpoint = error.config?.url?.includes('/invitations/')
        if (!isInvitationEndpoint) {
          logger.warn('⚠️ Authentication required (401)')
          // Note: AdminLayout and InspectorLayout handle authentication redirects
          // We don't redirect here to avoid conflicts and double redirects
        }
      } else if (error.response?.status === 403) {
        const errorDetail = error.response?.data?.detail?.toLowerCase() || ''

        // Check if this is a CSRF token error
        if (errorDetail.includes('csrf') && !error.config._csrfRetry) {
          logger.warn('🔄 CSRF token error - attempting to refresh and retry')

          try {
            // Make a GET request to refresh the CSRF token
            await client.get('/health')

            // Get the fresh CSRF token
            const freshToken = getCSRFToken()
            if (freshToken) {
              // Mark this request as a retry to prevent infinite loops
              error.config._csrfRetry = true
              error.config.headers['X-CSRF-Token'] = freshToken
              logger.debug('🔒 Retrying with fresh CSRF token')

              // Retry the original request
              return client.request(error.config)
            }
          } catch (refreshError) {
            logger.error('❌ Failed to refresh CSRF token:', refreshError)
          }
        } else {
          const detail = error.response?.data?.detail || 'Access denied'
          logger.warn(`⚠️ Access denied (403): ${detail}`)
        }
      } else if (error.response?.status === 429) {
        logger.warn('⚠️ Rate limit exceeded - slowing down requests')
      }

      return Promise.reject(error)
    }
  )

  return client
}

// Create the API client instance
export const apiClient = createApiClient()

// Module-scoped ref holding the latest X-Request-Id response header. Consumed
// by feedback context capture so submissions can correlate a server log line.
// Read-only side effect of the response interceptor - never widen this scope.
let lastRequestId: string | null = null
export const getLastRequestId = (): string | null => lastRequestId

// API response types
export interface HealthResponse {
  status: string
  timestamp: string
  version: string
  environment: string
  services: Record<string, {
    status: string
    response_time_ms: number
    message: string
    last_check: string
  }>
}

export interface FranchisePartner {
  id: number
  fp_code: string
  franchise_name: string
  country: string
  state: string
  system_status: string
  fp_status: string
  field_management_system: string
  lightspeed_store_code?: string
  lightspeed_domain?: string
  lightspeed_url?: string
  xero_short_code?: string
  resources?: FranchisePartnerResource[]
  outlet_count?: number
  // Data counts from sync (individual counts are deprecated, use total_data_count)
  products_count?: number
  sales_count?: number
  clients_count?: number
  xero_invoices_count?: number
  xero_payments_count?: number
  // Total data count from all systems (new cached field)
  total_data_count?: number
  // Last sync tracking fields
  last_sync_at?: string
  last_sync_system?: string
  last_sync_type?: string
}

export interface FranchisePartnerList {
  total_count: number
  enabled_count: number
  partners: FranchisePartner[]
}

export interface FranchisePartnerHealth {
  fp_code: string
  lightspeed_api_status: string
  xero_api_status: string
  field_management_status: string
  last_successful_sync?: string
  error_count_24h: number
}

export interface FranchisePartnerSearchResult {
  id: number
  fp_code: string
  franchise_name: string
  country: string
  fp_status?: string
}

// Inspector API types
export interface OutletAverageCost {
  outlet_id: string
  outlet_name: string
  average_cost: number
  inventory_level: number
  
  // Margin calculation fields
  margin_percentage?: number | null
  margin_category?: 'negative' | 'low' | 'good' | null
  margin_color_hint?: 'red' | 'orange' | 'default' | null
}

export interface ProductVariant {
  ls_id: string
  name: string
  variant_name?: string
  sku?: string
  handle?: string
  price_including_tax: number
  supply_price: number
  active: boolean
  has_inventory: boolean
  lightspeed_edit_url: string
  last_sale_date?: string
  last_received_date?: string
  outlet_costs: OutletAverageCost[]
}

export interface Product {
  ls_id: string
  name: string
  sku?: string
  handle?: string
  price_including_tax: number
  supply_price: number
  active: boolean
  has_inventory: boolean
  has_variants: boolean
  variant_count?: number
  description?: string
  image_url?: string
  lightspeed_edit_url: string
  last_sale_date?: string
  last_received_date?: string
  outlet_costs: OutletAverageCost[]
  variants: ProductVariant[]
}

export interface ProductsResponse {
  fp_code: string
  total_count: number
  parent_products_count: number
  variant_products_count: number
  outlets: string[]
  page: number
  page_size: number
  total_pages: number
  products: Product[]
}

export interface ExceptionType {
  id: number
  code: string
  data_domain: string
  exception_type?: string
  business_area?: string
  name_short: string
  name: string
  overview?: string
  why_flagged?: string
  search_keywords?: string[]
  visible_to_roles?: string[] | null
  count: number
  health_score?: {
    band: 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured'
    color: 'green' | 'orange' | 'red' | 'black' | 'grey'
    score: number | null
    metrics: {
      exception_count: number
      total_sample_size: number
      measure_type: string
      sample_type: string
      sample_value?: number
    } | null
  }
}

export interface ExceptionSale {
  ls_id: string
  invoice_number?: string
  sale_date?: string
  customer_id?: string
  status: string
  total_amount: number
  payments_total: number
  payment_count: number
  discrepancy: number
  
  // External system links
  lightspeed_sale_link: string
  lightspeed_ledger_link?: string
  accounts_transaction_id?: string
  xero_invoice_link?: string
  
  // Enhanced customer details
  customer_first_name?: string
  customer_last_name?: string
  customer_company?: string
  customer_email?: string
}

export interface ExceptionTypesResponse {
  exception_types: ExceptionType[]
  search_aliases?: Record<string, string[]>
}

export interface ExceptionSalesResponse {
  sales: ExceptionSale[]
  total: number
  limit: number
  offset: number
}

// Duplicate Sales — canonical shapes live in services/types/sales.ts;
// re-exported here so API-consumer call sites can keep their single import.
export type {
  DuplicateSaleGroupPreview,
  DuplicateSaleExceptionRow,
  DuplicateSaleExceptionsResponse,
} from './types/sales'

export interface ExceptionProduct {
  ls_id: string
  name: string
  sku?: string
  handle?: string
  price_including_tax: number
  supply_price: number
  category_name?: string
  outlet_costs: OutletAverageCost[]
  lightspeed_product_link: string
}

export interface ExceptionProductsResponse {
  products: ExceptionProduct[]
  total: number
  limit: number
  offset: number
  categories?: Array<{ id: string; name: string; category_path?: string }>
  outlets?: Array<{ id: string; name: string }>
}

export interface ExceptionSystem {
  id: string
  name: string
  status: string
  last_sync?: string
  sync_status?: string
  error_message?: string
  connection_status?: string
}

export interface ExceptionSystemsResponse {
  systems: ExceptionSystem[]
  total: number
  limit: number
  offset: number
}

export interface SystemConnection {
  id: number
  system_name: string
  connection_id: string
  secret_name: string
  metadata: {
    tenant_name?: string
    tenant_id?: string
    store_code?: string
    api_account_id?: string
    last_connected?: string
    connection_method?: string
    scopes?: string[]
  }
  created_at: string
  updated_at: string
  status?: 'connected' | 'disconnected' | 'error'
}

// API functions
export const healthApi = {
  getHealth: (): Promise<AxiosResponse<HealthResponse>> =>
    apiClient.get('/health/detail'),
    
  getReadiness: (): Promise<AxiosResponse<{ status: string; timestamp: string }>> =>
    apiClient.get('/health/ready'),
    
  getLiveness: (): Promise<AxiosResponse<{ status: string; timestamp: string }>> =>
    apiClient.get('/health/live'),
}

export const franchisePartnersApi = {
  getAll: (params?: { region?: string; enabled_only?: boolean }): Promise<AxiosResponse<FranchisePartnerList>> =>
    apiClient.get('/franchise-partners', { params }),
    
  search: (params?: { q?: string; status?: string }): Promise<AxiosResponse<FranchisePartnerSearchResult[]>> =>
    apiClient.get('/franchise-partners/search', { params }),
    
  getById: (fpCode: string): Promise<AxiosResponse<FranchisePartner>> =>
    apiClient.get(`/franchise-partners/${fpCode}`),
    
  getHealth: (fpCode: string): Promise<AxiosResponse<FranchisePartnerHealth>> =>
    apiClient.get(`/franchise-partners/${fpCode}/health`),
    
  enable: (fpCode: string): Promise<AxiosResponse<{ message: string; enabled: boolean }>> =>
    apiClient.post(`/franchise-partners/${fpCode}/enable`),
    
  disable: (fpCode: string): Promise<AxiosResponse<{ message: string; enabled: boolean }>> =>
    apiClient.post(`/franchise-partners/${fpCode}/disable`),
    
  validate: (fpCode: string): Promise<AxiosResponse<{
    fp_code: string
    lightspeed_api: string
    xero_api: string
    field_management_api: string
    validation_passed: boolean
  }>> =>
    apiClient.get(`/franchise-partners/${fpCode}/validate`),
}

// Xero Contact types
export interface XeroContact {
  id: number
  xero_contact_id: string
  contact_number?: string
  name: string
  email_address?: string
  first_name?: string
  last_name?: string
  is_customer: boolean
  is_supplier: boolean
  contact_status?: string
  address?: {
    line1?: string
    line2?: string
    city?: string
    region?: string
    postal_code?: string
    country?: string
  }
  phone?: string
  tax_number?: string
  updated_at: string
}

export interface XeroContactsResponse {
  contacts: XeroContact[]
  total_count: number
  page: number
  page_size: number
  has_more: boolean
  last_sync_at?: string
}

export const inspectorApi = {
  getProducts: (fpCode: string, params?: { page?: number; page_size?: number }): Promise<AxiosResponse<ProductsResponse>> =>
    apiClient.get(`/inspector/inventory/${fpCode}`, { params }),
    
  searchProducts: (fpCode: string, query: string, params?: { page?: number; page_size?: number }): Promise<AxiosResponse<ProductsResponse>> =>
    apiClient.get(`/inspector/inventory/${fpCode}/search`, { params: { query, ...params } }),
    
  getSalesExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/sales/exceptions/types/${fpCode}`),
    
  getSalesExceptions: (fpCode: string, exceptionCode: string, params?: { limit?: number; offset?: number }): Promise<AxiosResponse<ExceptionSalesResponse>> =>
    apiClient.get(`/inspector/sales/exceptions/${exceptionCode}/${fpCode}`, { params }),
    
  getStockManagementExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/stock-management/exceptions/types/${fpCode}`),

  getStockManagementExceptions: (fpCode: string, exceptionCode: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<AxiosResponse<any>> =>
    apiClient.get(`/inspector/stock-management/exceptions/${exceptionCode}/${fpCode}`, { params }),

  getProductSetupExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/product-setup/exceptions/types/${fpCode}`),

  getProductSetupExceptions: (fpCode: string, exceptionCode: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<AxiosResponse<any>> =>
    apiClient.get(`/inspector/product-setup/exceptions/${exceptionCode}/${fpCode}`, { params }),

  getProductPricingExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/product-pricing/exceptions/types/${fpCode}`),

  getProductPricingExceptions: (fpCode: string, exceptionCode: string, params?: {
    limit?: number;
    offset?: number;
  }): Promise<AxiosResponse<any>> =>
    apiClient.get(`/inspector/product-pricing/exceptions/${exceptionCode}/${fpCode}`, { params }),
    
  getSystemsExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/systems/exceptions/types/${fpCode}`),

  getSystemsExceptions: (fpCode: string, exceptionCode: string, params?: { limit?: number; offset?: number }): Promise<AxiosResponse<any>> =>
    apiClient.get(`/inspector/systems/exceptions/${exceptionCode}/${fpCode}`, { params }),

  getClientExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/client/exceptions/types/${fpCode}`),

  getClientExceptions: (fpCode: string, exceptionCode: string, params?: { limit?: number; offset?: number }): Promise<AxiosResponse<any>> =>
    apiClient.get(`/inspector/client/exceptions/${exceptionCode}/${fpCode}`, { params }),

  getAccountingExceptionTypes: (fpCode: string): Promise<AxiosResponse<ExceptionTypesResponse>> =>
    apiClient.get(`/inspector/accounting/exceptions/types/${fpCode}`),

  getAccountingExceptions: (fpCode: string, exceptionCode: string, params?: { limit?: number; offset?: number }): Promise<AxiosResponse<any>> =>
    apiClient.get(`/inspector/accounting/exceptions/${exceptionCode}/${fpCode}`, { params }),

  getXeroContacts: (fpCode: string, params?: {
    page?: number
    page_size?: number
    search?: string
    contact_type?: 'all' | 'customers' | 'suppliers'
    sort_by?: 'name' | 'email' | 'updated_at'
    sort_order?: 'asc' | 'desc'
  }): Promise<AxiosResponse<XeroContactsResponse>> =>
    apiClient.get(`/inspector/xero/contacts/${fpCode}`, { params }),

  // TEMPORARY: Invoice Payments Export - Remove after February 2025
  exportInvoicePayments: (fpCode: string): Promise<AxiosResponse<Blob>> =>
    apiClient.get(`/inspector/sales/tools/invoice-payments-export/${fpCode}`, { 
      responseType: 'blob' 
    }),
    
  // Sale Tools - Duplicate Sales (one row per duplicate group)
  getDuplicateSales: (
    fpCode: string,
  ): Promise<AxiosResponse<DuplicateSaleExceptionsResponse>> =>
    apiClient.get(`/inspector/sales/tools/duplicate-sales/${fpCode}?limit=500`),
}

export const systemConnectionsApi = {
  getConnections: (fpCode: string): Promise<AxiosResponse<SystemConnection[]>> =>
    apiClient.get(`/systems/connections/${fpCode}`),
    
  addConnection: (fpCode: string, connectionData: any): Promise<AxiosResponse<SystemConnection>> =>
    apiClient.post(`/systems/connections/${fpCode}`, connectionData),
    
  updateConnection: (fpCode: string, connectionId: string, connectionData: any): Promise<AxiosResponse<SystemConnection>> =>
    apiClient.put(`/systems/connections/${fpCode}/${connectionId}`, connectionData),
    
  deleteConnection: (fpCode: string, connectionId: string): Promise<AxiosResponse<{ message: string }>> =>
    apiClient.delete(`/systems/connections/${fpCode}/${connectionId}`),
}

// Sync-related types and interfaces
export interface SyncOperation {
  operation_id: string
  fp_code: string
  franchise_name: string
  sync_system: string
  operation_type: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  progress_percentage: number
  current_step: string
  records_processed: number
  records_total?: number
  records_created: number
  records_updated: number
  records_deleted: number
  started_at?: string
  completed_at?: string
  duration_seconds?: number
  elapsed_seconds?: number
  // Lock heartbeat fields for staleness detection
  last_heartbeat?: string
  heartbeat_age_seconds?: number
  heartbeat_status?: 'healthy' | 'aging' | 'stale' | 'no_lock' | null
  error_message?: string
  initiated_by: string
  created_at: string
  worker_memory_mb?: number
  // Memory stats from completed syncs
  memory_start_mb?: number
  memory_end_mb?: number
  memory_delta_mb?: number
  sync_metadata?: {
    [system: string]: {
      [dataType: string]: {
        created: number
        updated: number
        deleted: number
      }
    }
  }
}

// Sync Lock interfaces
export interface AdvisoryLock {
  lock_hash: number
  lock_key?: string
  operation_id?: string
  worker_id?: string
  created_at?: string
  last_heartbeat?: string
  heartbeat_age_minutes?: number
  age_minutes?: number
  is_sync_lock: boolean
  fp_code?: string
  system?: string
  appears_stuck: boolean
}

export interface LockListResponse {
  locks: AdvisoryLock[]
  total_locks: number
  sync_locks: AdvisoryLock[]
  stuck_locks: AdvisoryLock[]
  timestamp: string
}

export interface LockReleaseRequest {
  reason: string
  confirm: boolean
}

export interface LockCleanupResponse {
  success: boolean
  cleanup_stats: {
    stale_locks_removed: number
  }
  total_cleaned: number
  message: string
  timestamp: string
}

export interface SyncStats {
  active_syncs: number
  completed_today: number
  failed_today: number
  cancelled_today: number
  completed_with_errors_today: number
  total_records_synced_today: number
  average_duration_minutes?: number
  system_health: Record<string, string>
  total_completed: number
  total_failed: number
  total_cancelled: number
  total_completed_with_errors: number
}

export interface SyncHistoryFilters {
  fp_code?: string
  system?: string
  status?: string[]
  days?: number
  limit?: number
  offset?: number
  search?: string
  initiated_by?: string
}

export interface SyncHistoryResponse {
  operations: SyncOperation[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

// Sync API functions
export const syncApi = {
  // Get sync statistics
  getStats: (days?: number): Promise<AxiosResponse<SyncStats>> => {
    const params = new URLSearchParams()
    if (days) params.append('days', days.toString())
    const queryString = params.toString()
    const url = queryString ? `/sync/stats?${queryString}` : '/sync/stats'
    return apiClient.get(url)
  },

  // Get unique initiators for filter dropdown
  getInitiators: (): Promise<AxiosResponse<{ initiators: string[] }>> =>
    apiClient.get('/sync/initiators'),

  // Get sync history with filtering and pagination
  getHistory: (filters?: SyncHistoryFilters): Promise<AxiosResponse<SyncHistoryResponse>> => {
    const params = new URLSearchParams()
    if (filters?.fp_code) params.append('fp_code', filters.fp_code)
    if (filters?.system) params.append('system', filters.system)
    if (filters?.status) filters.status.forEach(s => params.append('status', s))
    if (filters?.days) params.append('days', filters.days.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.offset) params.append('offset', filters.offset.toString())
    if (filters?.initiated_by) params.append('initiated_by', filters.initiated_by)

    const queryString = params.toString()
    const url = queryString ? `/sync/history?${queryString}` : '/sync/history'
    return apiClient.get(url)
  },
  
  // Get active syncs for a specific FP
  getActiveSyncs: (fpCode: string): Promise<AxiosResponse<SyncOperation[]>> =>
    apiClient.get(`/sync/operations/active/${fpCode}`),
    
  // Get all active syncs (admin view)
  getAllActiveSyncs: (): Promise<AxiosResponse<SyncOperation[]>> =>
    apiClient.get('/sync/operations/active'),
    
  // Get sync progress for specific operation
  getProgress: (operationId: string): Promise<AxiosResponse<SyncOperation>> =>
    apiClient.get(`/sync/progress/${operationId}`),
    
  // Cancel sync operation
  cancelSync: (operationId: string, reason?: string): Promise<AxiosResponse<{ message: string }>> =>
    apiClient.post(`/sync/cancel_sync/${operationId}`, { reason: reason || 'Manual cancellation' }),
    
  // Start sync operation
  startSync: (system: string, fpCode: string, syncType: string = 'smart'): Promise<AxiosResponse<any>> =>
    apiClient.post(`/sync/${system}/${fpCode}`, { sync_type: syncType, priority: 1 }),
    
  // Lock management endpoints

  // List all sync locks
  getLocks: (): Promise<AxiosResponse<LockListResponse>> =>
    apiClient.get('/sync/locks'),
    
  // Force release a specific sync lock
  releaseLock: (fpCode: string, system: string, reason: string = 'Manual release via admin panel'): Promise<AxiosResponse<any>> =>
    apiClient.delete(`/sync/locks/${fpCode}/${system}`, {
      data: { reason, confirm: true }
    }),
    
  // Cleanup stale locks automatically
  cleanupLocks: (): Promise<AxiosResponse<LockCleanupResponse>> =>
    apiClient.post('/sync/locks/cleanup', {}),

  // Bulk sync operations
  bulkTriggerSync: (data: {
    fp_codes: string[]
    systems: string[]
    sync_type: string
    priority: number
    confirm_text: string
  }): Promise<AxiosResponse<any>> =>
    apiClient.post('/sync/bulk-trigger', data),
}

// Data management APIs
export const dataApi = {
  // Get unified data type definitions
  getDataTypes: (): Promise<AxiosResponse<any>> =>
    apiClient.get('/admin/data-types'),
    
  // Get data types for a specific system
  getSystemDataTypes: (system: string): Promise<AxiosResponse<any[]>> =>
    apiClient.get(`/admin/data-types/${system}`),
    
  // Clear data for a single system
  clearSystemData: (system: string, fpCode: string, data: {
    reason: string
    confirm_deletion: boolean
    dry_run: boolean
  }): Promise<AxiosResponse<any>> =>
    apiClient.post(`/sync/clear-data/${fpCode}`, {
      systems: [system],
      ...data
    }),
    
  // Clear data for multiple systems
  clearMultiSystemData: (fpCode: string, data: {
    systems: string[]
    reason: string
    confirm_deletion: boolean
    dry_run: boolean
  }): Promise<AxiosResponse<any>> =>
    apiClient.post(`/sync/clear-data/${fpCode}`, data),

  // Bulk clear data for multiple franchises
  bulkClearData: (data: {
    fp_codes: string[]
    systems: string[]
    reason: string
    confirm_text: string
    dry_run: boolean
    trigger_sync: boolean
  }): Promise<AxiosResponse<any>> =>
    apiClient.post('/sync/bulk-clear-data', data),
}

// Utility functions
export const handleApiError = (error: any): string => {
  if (error.response?.status === 422) {
    // Handle Pydantic validation errors
    const data = error.response.data
    if (Array.isArray(data?.detail)) {
      // Extract error messages from validation error array
      const messages = data.detail.map((err: any) => {
        const field = err.loc?.join('.') || 'field'
        return `${field}: ${err.msg}`
      })
      return messages.join(', ')
    } else if (typeof data?.detail === 'string') {
      return data.detail
    }
    return 'Validation error: Please check your input'
  }

  if (error.response?.data?.detail) {
    // Handle detail if it's an object (shouldn't be rendered as React children)
    if (typeof error.response.data.detail === 'object') {
      return JSON.stringify(error.response.data.detail)
    }
    return error.response.data.detail
  }
  
  if (error.response?.status === 403) {
    return 'Access denied: You do not have permission to access this resource'
  }
  
  if (error.response?.status === 404) {
    return 'Not found: The requested resource does not exist'
  }
  
  if (error.response?.status >= 500) {
    return 'Server error: Please try again later'
  }
  
  if (error.code === 'ECONNABORTED') {
    return 'Request timeout: The server is taking too long to respond'
  }
  
  return error.message || 'An unexpected error occurred'
}

// Generic API wrapper for convenience
export const api = {
  get: async <T = any>(url: string, config?: any): Promise<T> => {
    const response = await apiClient.get(url, config)
    return response.data
  },

  post: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.post(url, data, config)
    return response.data
  },

  put: async <T = any>(url: string, data?: any, config?: any): Promise<T> => {
    const response = await apiClient.put(url, data, config)
    return response.data
  },

  delete: async <T = any>(url: string, config?: any): Promise<T> => {
    const response = await apiClient.delete(url, config)
    return response.data
  },

  // Export franchise partners to Excel
  exportFranchisePartners: async (filters?: { market?: string; systemStatus?: string; fpStatus?: string; search?: string }): Promise<Blob> => {
    const params = new URLSearchParams()
    if (filters?.market) params.append('market', filters.market)
    if (filters?.systemStatus) params.append('system_status', filters.systemStatus)
    if (filters?.fpStatus) params.append('fp_status', filters.fpStatus)
    if (filters?.search) params.append('search', filters.search)

    const queryString = params.toString()
    const url = queryString ? `/admin/franchise-partners/export?${queryString}` : '/admin/franchise-partners/export'

    const response = await apiClient.get(url, { responseType: 'blob' })
    return response.data
  }
}

// Export default API client
export default apiClient
